/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useCallback, useLayoutEffect, useContext, useRef } from "react";
import { StateContext } from "../../../../context/globalContext/context";
import { iGlobalContext } from "../../../../context/globalContext/interface";
import { ROUTES } from "../../../../constant";
import useMediaQuery from "@mui/material/useMediaQuery";
import { isPortraitLockActive } from "../../../../utils/portraitLock";
import { MOBILE_MEDIA_QUERY } from "../../../../constant/breakpoints";

export const useGlobalEvents = () => {
  const { setIsMobile, isOpenMobileSidebar, isMobile, setIosNativeLoginData }: iGlobalContext =
    useContext(StateContext);

  const localIsMobile = useMediaQuery(MOBILE_MEDIA_QUERY);
  const pathname = window.location.pathname; // or useLocation if calling inside Router context, which it is.

  const checkMobileView = useCallback(() => {
    if (isPortraitLockActive()) return;
    if (window.matchMedia(MOBILE_MEDIA_QUERY).matches) {
      setIsMobile(true);
    } else {
      setIsMobile(false);
    }
  }, [setIsMobile]);

  const handleMobileUpdate = useCallback(() => {
    // Same rationale as checkMobileView above — even though this only sets `true`, we
    // skip during the overlay so the resize batch is fully idempotent from the user's
    // perspective. Avoids spurious context-state writes on every rotation tremor.
    if (isPortraitLockActive()) return;
    setIsMobile(true);
  }, [setIsMobile]);

  useLayoutEffect(() => {
    const isHomeOrTopDestination =
      pathname === ROUTES.Home ||
      pathname === ROUTES.TopDestination ||
      pathname === ROUTES.Faqs ||
      pathname === ROUTES.AboutUs ||
      pathname === ROUTES.PaymentSuccess ||
      pathname === ROUTES.ModifyHotelsSuccess ||
      ROUTES.PaymentFailure;
    const isResetPasswordRoute = pathname.startsWith("/resetpassword/");
    const updateFunction = isHomeOrTopDestination || isResetPasswordRoute ? checkMobileView : handleMobileUpdate;

    window.addEventListener("resize", updateFunction);
    updateFunction();

    return () => window.removeEventListener("resize", updateFunction);
  }, [checkMobileView, handleMobileUpdate, pathname]);

  useEffect(() => {
    if (isOpenMobileSidebar && isMobile) {
      document.documentElement.style.overflowY = "hidden";
    } else {
      document.documentElement.style.overflowY = "auto";
    }
    return () => {
      document.documentElement.style.overflowY = "auto";
    };
  }, [isMobile, isOpenMobileSidebar]);

  const lastTabLeaveTimeRef: any = useRef(null);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        // User is leaving the tab, record the time
        lastTabLeaveTimeRef.current = Date.now();
      } else if (document.visibilityState === "visible") {
        // User has returned to the tab
        const now = Date.now();
        const timeAway = now - (lastTabLeaveTimeRef.current || now);

        // Check if user was away for 15 minutes (900,000 ms)
        if (timeAway >= 900000 && localIsMobile) {
          window.location.reload();
        }

        // Reset the last leave time
        lastTabLeaveTimeRef.current = null;
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  // Flutter bridge

  (window as any).fromFlutter = function (data: any) {
    const params = data;
    if (typeof params === "string") setIosNativeLoginData(JSON.parse(data));
    else setIosNativeLoginData(data);
  };

  return { localIsMobile };
};
